import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem as MuiMenuItem,
  menuItemClasses,
  styled,
  TextField,
} from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';

import { useEmailViewContext } from '../context/EmailView.context.hooks';

export const AddressForm = () => {
  const { addressFormik } = useEmailViewContext();

  return (
    <Box
      sx={{
        paddingTop: 1.5,
      }}
    >
      <form onSubmit={addressFormik.handleSubmit}>
        <FromInput
          value={addressFormik.values.from}
          onChange={value => addressFormik.setFieldValue('from', value)}
        />
        <ToInput
          value={addressFormik.values.to}
          onChange={value => addressFormik.setFieldValue('to', value)}
        />
      </form>
    </Box>
  );
};

interface FromInputProps {
  value: string;
  onChange: (value: string) => void;
}

const FromInput = ({ value, onChange }: FromInputProps) => {
  return (
    <InputBase
      fullWidth
      startAdornment={
        <InputAdornment
          position="start"
          sx={{
            color: theme => theme.palette.text.disabled,
          }}
        >
          From:{' '}
        </InputAdornment>
      }
      sx={{
        paddingY: 1.2,
        paddingX: 4.5,
        borderBottom: theme =>
          `1px solid ${theme.palette.action.disabledBackground}`,
        color: theme => theme.palette.primary.main,
        fontWeight: 400,
        fontSize: 14,
      }}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  );
};

interface ToInputProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const ToInput = ({ value, onChange }: ToInputProps) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        paddingY: 0.9,
        borderBottom: theme =>
          `1px solid ${theme.palette.action.disabledBackground}`,
      }}
    >
      <Autocomplete
        multiple
        freeSolo
        fullWidth
        options={[]}
        defaultValue={[]}
        renderTags={(value: readonly string[], getTagProps: any) => {
          return value.map((option: string, index: number) => (
            <EmailChip label={option} {...getTagProps({ index })} />
          ));
        }}
        renderInput={params => (
          <TextField
            {...params}
            sx={{
              paddingLeft: 4.5,
              paddingRight: 1,
              boxSizing: 'border-box',
              '.MuiOutlinedInput-root': {
                padding: 0,
              },
              '.MuiOutlinedInput-notchedOutline': {
                borderWidth: `0px !important`,
              },
            }}
            InputProps={{
              ...params.InputProps,
              sx: {
                color: theme => theme.palette.primary.main,
                fontWeight: 400,
                fontSize: 14,
              },
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{
                      color: theme => theme.palette.text.disabled,
                    }}
                  >
                    To:{' '}
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        value={value}
        onChange={(event, value) => {
          onChange(value);
        }}
        inputValue={inputValue}
        onInputChange={(event, inputValue) => {
          const options = inputValue.split(/[, ]+/);

          if (options.length > 1) {
            onChange(
              value
                .concat(options)
                .map(x => x?.trim())
                .filter(x => x)
            );

            setInputValue('');
          } else {
            setInputValue(inputValue);
          }
        }}
        onBlur={(event: any) => {
          const option = (event?.target?.value || '').trim();

          if (option) {
            onChange([...(value || []), option]);
            setInputValue('');
          }
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingRight: 4.5,
          paddingLeft: 1,
        }}
      >
        <Button
          sx={{
            fontWeight: 400,
            textTransform: 'unset',
            minWidth: 'unset',
          }}
        >
          Cc
        </Button>

        <Button
          sx={{
            fontWeight: 400,
            textTransform: 'unset',
            minWidth: 'unset',
            marginLeft: 2,
          }}
        >
          Bcc
        </Button>
      </Box>
    </Box>
  );
};

interface EmailChipProps {
  label?: string;
}

const EmailChip = ({ label = 'No email' }: EmailChipProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Button
        endIcon={
          <ArrowDropDown
            sx={{
              marginLeft: -0.5,
            }}
          />
        }
        onClick={handleClick}
        sx={{
          fontWeight: 400,
          textTransform: 'unset',
          marginRight: 0.5,
        }}
      >
        {label}
      </Button>

      <Menu
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 120,
          },
        }}
      >
        <MenuItem onClick={handleClose}>Option 1</MenuItem>
        <MenuItem onClick={handleClose}>Option 2</MenuItem>
        <MenuItem onClick={handleClose}>Option 3</MenuItem>
      </Menu>
    </Box>
  );
};

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  [`&.${menuItemClasses.root}`]: {
    fontSize: 14,
    color: theme.palette.primary.main,
  },
}));
